/**
 * Тексты и форматирование дат журнала изменений и сводки (§8, §8.1, §11.7).
 *
 * Общий `web/src/texts.ts` трогать нельзя — он читается другими волнами, поэтому
 * строки этих двух экранов живут здесь. Формулировки из §12 берутся дословно;
 * всё, что спека не формулирует, написано в том же тоне и помечено в отчёте.
 *
 * Даты — только `Intl`, без библиотек: «5 минут назад», «вчера в 09:10»,
 * «14 октября в 16:42». Правило простое: чем свежее событие, тем человечнее
 * подпись, потому что в журнале смотрят в основном последний день.
 */
import type { Change, ChangeAction, ChannelCode, EntityType } from '@shared/types.ts'
import { countOf, forms, plural } from '../../texts.js'

// ─────────────────────────────── даты ───────────────────────────────────────

const fmtDayMonth = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' })
const fmtDayMonthYear = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
const fmtTime = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' })
const fmtShortDate = new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit' })

const MINUTE = 60_000
const HOUR = 60 * MINUTE

/** Разобрать ISO-строку. Непонятную дату не прячем за «—», а возвращаем null. */
export function parseDate(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : date
}

function startOfDay(date: Date): number {
  const copy = new Date(date)
  copy.setHours(0, 0, 0, 0)
  return copy.getTime()
}

/** Сколько календарных суток назад: 0 — сегодня, 1 — вчера. */
function daysAgo(date: Date, now: Date): number {
  return Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000)
}

/** «только что» · «5 минут назад» · «сегодня в 16:42» · «вчера в 09:10» · «14 октября в 16:42». */
export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  const date = parseDate(iso)
  if (!date) return '—'

  const diff = now.getTime() - date.getTime()
  if (diff >= 0 && diff < MINUTE) return 'только что'
  if (diff >= 0 && diff < HOUR) {
    const minutes = Math.max(1, Math.floor(diff / MINUTE))
    return `${minutes} ${plural(minutes, ['минуту', 'минуты', 'минут'])} назад`
  }

  const days = daysAgo(date, now)
  if (days === 0) return `сегодня в ${fmtTime.format(date)}`
  if (days === 1) return `вчера в ${fmtTime.format(date)}`
  if (date.getFullYear() === now.getFullYear()) return `${fmtDayMonth.format(date)} в ${fmtTime.format(date)}`
  return `${fmtDayMonthYear.format(date)} в ${fmtTime.format(date)}`
}

/** «14.10 17:05» — колонка «Когда» в журнале (§8). */
export function formatTableWhen(iso: string | null | undefined): string {
  const date = parseDate(iso)
  if (!date) return '—'
  return `${fmtShortDate.format(date)} ${fmtTime.format(date)}`
}

/** «14.10 в 16:42» — подзаголовок пакета (§12.2). */
export function formatShortDateTime(iso: string | null | undefined): string {
  const date = parseDate(iso)
  if (!date) return '—'
  return `${fmtShortDate.format(date)} в ${fmtTime.format(date)}`
}

/** «2 октября, 11:40» — заголовок сводки (§12.2). */
export function formatDateComma(iso: string | null | undefined): string {
  const date = parseDate(iso)
  if (!date) return '—'
  const head = date.getFullYear() === new Date().getFullYear()
    ? fmtDayMonth.format(date)
    : fmtDayMonthYear.format(date)
  return `${head}, ${fmtTime.format(date)}`
}

/** «2 октября» — пункт «предыдущий визит» в селекте периода (§8.1). */
export function formatDayMonth(iso: string | null | undefined): string {
  const date = parseDate(iso)
  if (!date) return '—'
  return fmtDayMonth.format(date)
}

// ─────────────────────── подписи правок и полей ─────────────────────────────

/** Действие без поля: «Бренд создан», «Категория отвязана». */
const ACTION_LABELS: Record<ChangeAction, string> = {
  create: 'Создано',
  update: 'Правка',
  archive: 'Бренд архивирован',
  restore: 'Бренд восстановлен',
  attach: 'Категория привязана',
  detach: 'Категория отвязана',
  comment: 'Комментарий',
  delete_comment: 'Комментарий удалён',
}

const CREATE_LABELS: Partial<Record<EntityType, string>> = {
  brand: 'Бренд создан',
  brand_channel: 'Бренд добавлен в канал',
  brand_category: 'Категория привязана',
  category: 'Категория создана',
  enum_value: 'Значение справочника создано',
  field_definition: 'Поле создано',
  user: 'Пользователь создан',
  comment: 'Комментарий',
}

/** Человеческая подпись строки правки: поле, а если поля нет — действие. */
/**
 * Служебные поля в историю попадают техническими ключами: их пишет не форма с
 * подписями, а код — выдача кода входа, смена пароля, архивация. В журнале,
 * который читают люди, «login_code» и «is_active» выглядят как сбой.
 */
const TECHNICAL_LABELS: Record<string, string> = {
  login_code: 'Код входа',
  password: 'Пароль',
  status: 'Статус',
  is_active: 'Доступ',
  role_id: 'Роль',
  full_name: 'ФИО',
  short_name: 'Короткое имя',
  email: 'Почта',
  job_title: 'Должность',
  name: 'Название',
  sort_order: 'Порядок',
}

/** Значения тех же служебных полей — тоже коды. */
const TECHNICAL_VALUES: Record<string, string> = {
  active: 'активен',
  archived: 'в архиве',
  draft: 'черновик',
  committed: 'подтверждён',
  reverted: 'откачен',
  discarded: 'отменён',
  true: 'да',
  false: 'нет',
}

/** Код поля → подпись, если это известный служебный ключ. */
export function humanFieldLabel(label: string | null | undefined): string | null {
  if (!label) return null
  return TECHNICAL_LABELS[label] ?? label
}

/** Значение служебного поля → слово, если это известный код. */
export function humanValue(value: string): string {
  return TECHNICAL_VALUES[value] ?? value
}

export function changeRowLabel(change: Change): string {
  if (change.fieldLabel) return humanFieldLabel(change.fieldLabel) ?? change.fieldLabel
  if (change.action === 'create') return CREATE_LABELS[change.entityType] ?? ACTION_LABELS.create
  return ACTION_LABELS[change.action]
}

/** Правка без «было → стало»: привязка, архивация, удаление комментария. */
export function isStandaloneChange(change: Change): boolean {
  if (change.fieldLabel) return false
  return change.action !== 'update'
}

/** Значение для человека. Пустое всегда пишется словом «пусто» (§6.3). */
export function valueLabel(label: string | null, value: unknown): string {
  if (label !== null && label !== '') return humanValue(label)
  if (value === null || value === undefined || value === '') return 'пусто'
  if (typeof value === 'boolean') return value ? 'Да' : 'Нет'
  if (typeof value === 'number') return String(value)
  /* Голые коды статусов («active», «archived») тоже переводим: подписи у таких
     значений нет, они приходят из служебных правок. */
  if (typeof value === 'string') return humanValue(value)
  return 'пусто'
}

/** Адрес внутри бренда: «Розница», «Духовые шкафы» — то, что идёт после « · ». */
export function changeAddress(change: Change, brandName: string | null): string | null {
  const label = change.entityLabel
  if (!label) return null
  const parts = label.split(' · ')
  if (parts.length < 2) return null
  const tail = parts.slice(1).join(' · ')
  if (brandName && parts[0] !== brandName) return tail
  return tail
}

// ───────────────────── группы полей для сводки (§8.1) ───────────────────────

export type FieldGroupCode =
  | 'tier' | 'categories' | 'seo' | 'context' | 'visibility' | 'url'
  | 'stock' | 'service' | 'note' | 'comments' | 'newBrands' | 'archive' | 'admin' | 'other'

export const fieldGroupNames: Record<FieldGroupCode, string> = {
  tier: 'Приоритеты',
  categories: 'Категории',
  seo: 'SEO',
  context: 'Контекст',
  visibility: 'Видимость',
  url: 'URL',
  stock: 'Наличие',
  service: 'Сервис',
  note: 'Примечания',
  comments: 'Комментарии',
  newBrands: 'Новые бренды',
  archive: 'Архивация',
  admin: 'Справочники и настройки',
  other: 'Прочее',
}

const ADMIN_ENTITIES: ReadonlySet<EntityType> = new Set<EntityType>([
  'category', 'enum_value', 'field_definition', 'user', 'role', 'setting', 'change_set',
])

/** К какой смысловой группе отнести правку в сводке «По типам» (§8.1). */
export function fieldGroupOf(change: Change): FieldGroupCode {
  if (ADMIN_ENTITIES.has(change.entityType)) return 'admin'
  if (change.entityType === 'comment') return 'comments'
  if (change.action === 'create' && change.entityType === 'brand') return 'newBrands'
  if (change.action === 'archive' || change.action === 'restore') return 'archive'
  if (change.action === 'attach' || change.action === 'detach') return 'categories'

  switch (change.fieldCode) {
    case 'tier': return 'tier'
    case 'seoPriority': return 'seo'
    case 'contextPriority': return 'context'
    case 'visibility': return 'visibility'
    case 'url': return 'url'
    case 'stockDisplay':
    case 'oos': return 'stock'
    case 'delivery':
    case 'installation':
    case 'warranty': return 'service'
    case 'note': return 'note'
    case 'isPresent': return 'categories'
    default: return 'other'
  }
}

// ─────────────────────── фильтры журнала: словари ───────────────────────────

/** Поля, по которым имеет смысл фильтровать журнал (§8, колонка «Поле»). */
export const historyFieldOptions: { value: string; label: string }[] = [
  { value: 'tier', label: 'Приоритет (тир)' },
  { value: 'visibility', label: 'Видимость на сайте' },
  { value: 'url', label: 'URL' },
  { value: 'seoPriority', label: 'Приоритет SEO' },
  { value: 'contextPriority', label: 'Приоритет Контекст' },
  { value: 'stockDisplay', label: 'Отображаемый статус наличия' },
  { value: 'oos', label: 'Статус «нет в наличии»' },
  { value: 'delivery', label: 'Доставка' },
  { value: 'installation', label: 'Установка' },
  { value: 'warranty', label: 'Гарантия' },
  { value: 'note', label: 'Примечание' },
  { value: 'isPresent', label: 'Присутствие в канале' },
]

/** Типы сущностей в журнале (§8, колонка «Сущность»). */
export const historyEntityOptions: { value: EntityType; label: string }[] = [
  { value: 'brand', label: 'Бренд' },
  { value: 'brand_channel', label: 'Бренд в канале' },
  { value: 'brand_category', label: 'Категория бренда' },
  { value: 'brand_category_channel', label: 'Категория в канале' },
  { value: 'comment', label: 'Комментарий' },
  { value: 'category', label: 'Справочник категорий' },
  { value: 'enum_value', label: 'Справочник значений' },
  { value: 'field_definition', label: 'Поле' },
  { value: 'user', label: 'Пользователь' },
  { value: 'setting', label: 'Настройка' },
]

// ─────────────────────────────── строки ─────────────────────────────────────

export const historyTexts = {
  /** §12.2 */
  title: 'История изменений',

  /** §8: колонки журнала */
  columns: {
    number: '№',
    when: 'Когда',
    author: 'Автор',
    note: 'Комментарий',
    channel: 'Канал',
    changes: 'Правок',
    telegram: 'Telegram',
    actions: 'Действия',
  },

  /** §8: быстрые чипы над таблицей */
  quick: {
    today: 'Сегодня',
    mine: 'Моё',
    reverts: 'Откаты',
    system: 'Системные',
  },

  filters: {
    heading: 'Фильтры',
    panelLabel: 'Фильтры журнала изменений',
    author: 'Автор',
    period: 'Период',
    brand: 'Бренд',
    field: 'Поле',
    entity: 'Сущность',
    channel: 'Канал',
    status: 'Статус',
    reset: 'Сбросить',
    anyAuthor: 'любой',
    anyPeriod: 'всё время',
    anyBrand: 'любой',
    anyField: 'любое',
    anyEntity: 'любая',
    anyChannel: 'любой',
    anyStatus: 'любой',
    brandSearchLabel: 'Поиск бренда в журнале',
    brandSearchPlaceholder: 'Название бренда',
    brandPickLabel: 'Выбрать бренд',
    brandNothing: (q: string) => `По запросу «${q}» брендов не нашлось`,
    clientSide: 'Статус и системные пакеты отбираются на загруженной странице',
  },

  period: {
    all: 'всё время',
    today: 'сегодня',
    d7: '7 дней',
    d30: '30 дней',
    month: 'этот месяц',
    visit: (day: string) => `предыдущий визит — ${day}`,
    visitUnknown: 'предыдущий визит — метки ещё нет',
  },

  status: {
    all: 'любой',
    committed: 'подтверждён',
    reverted: 'откатан',
    revert: 'это откат',
    system: 'системный',
  },

  /** §8: плашки статуса пакета */
  marks: {
    system: 'системный',
    systemHint: 'Пакет #1 — импорт всех данных из Google-таблицы',
    reverted: 'откатан',
    revertedBy: (id: number) => `откатан пакетом #${id}`,
    revert: 'откат',
    revertOf: (id: number) => `откат пакета #${id}`,
  },

  telegram: {
    sent: 'отправлено',
    pending: 'в очереди',
    failed: 'не отправилось',
    skipped: 'уведомления выключены',
    retry: 'Отправить повторно',
    retrying: 'Отправляем…',
    sentToast: 'Уведомление отправлено в Telegram',
    noRight: 'Повторная отправка доступна собственнику',
  },

  /** §8: откат пакета */
  revert: {
    action: 'Откатить пакет',
    openPack: 'Открыть пакет',
    systemDisabled: 'Системный пакет откатить нельзя',
    noRight: 'Нет права откатывать пакеты. Доступно коммерческому директору и собственнику',
    alreadyReverted: 'Пакет уже откатан',
    title: (id: number) => `Откатить пакет #${id}?`,
    counts: (id: number, changes: number) => `Пакет #${id} — ${countOf(changes, forms.change)}.`,
    overwritten: (n: number) =>
      `${n} ${plural(n, ['из них уже перезаписана', 'из них уже перезаписаны', 'из них уже перезаписаны'])} позже — откат вернёт прежние значения поверх новых.`,
    consequence: 'Откат публикуется сразу отдельным пакетом: правки увидят все и в чат уйдёт одно сообщение.',
    confirm: 'Откатить пакет',
    cancel: 'Отмена',
    done: (id: number, source: number) => `Пакет #${id} — откат пакета #${source} опубликован`,
  },

  /** §8: выгрузка журнала */
  exportXlsx: '⬇ XLSX',
  exportCsv: '⬇ CSV',
  exportSoon: 'Выгрузка журнала по фильтрам появится вместе с серверной частью',

  /** §8: раскрытие пакета */
  expand: 'Раскрыть пакет',
  collapse: 'Свернуть пакет',
  expandLabel: (id: number) => `Раскрыть пакет #${id}`,
  collapseLabel: (id: number) => `Свернуть пакет #${id}`,
  authorFilterLabel: (name: string) => `Фильтр: автор — ${name}`,
  rowActionsLabel: (id: number) => `Действия с пакетом #${id}`,
  systemAuthor: 'Импорт из Google-таблицы',
  unknownAuthor: 'Неизвестный автор',
  noNote: '—',

  /** §8: счётчики и страницы */
  countsCell: (changes: number, brands: number) =>
    brands > 0 ? `${changes} / ${countOf(brands, forms.brand)}` : String(changes),
  countsCellLabel: (changes: number, brands: number) =>
    brands > 0
      ? `${countOf(changes, forms.change)} в ${countOf(brands, forms.brand)}`
      : countOf(changes, forms.change),
  shown: (from: number, to: number, total: number) => `Показано ${from}–${to} из ${total}`,
  shownFiltered: (n: number, loaded: number) => `Отобрано ${n} из ${loaded} на странице`,
  prev: 'Назад',
  next: 'Дальше',
  prevPage: 'Предыдущая страница',
  nextPage: 'Следующая страница',
  pageOf: (page: number, pages: number) => `Страница ${page} из ${pages}`,

  /** §12.8 и §11.3 */
  emptyTitle: 'Пакетов по этим фильтрам нет',
  emptyReset: 'Сбросить',
  errorTitle: 'Не удалось загрузить историю',
  retry: 'Повторить',
  loadingLabel: 'Загружаем историю изменений',

  /** §8: экран одного пакета */
  pack: {
    back: 'История изменений',
    title: (id: number | string) => `Пакет #${id}`,
    subtitle: (author: string, when: string, changes: number, channel: string | null) =>
      [author, when, countOf(changes, forms.change), channel].filter(Boolean).join(' · '),
    noteLabel: 'Комментарий к пакету',
    noteEmpty: 'Комментария к пакету нет',
    notFoundTitle: 'Пакет не найден',
    notFoundText: 'Возможно, ссылка устарела или номер набран с ошибкой.',
    toHistory: 'Открыть журнал',
  },
} as const

/** §11.7 «Данные обновлены другим пользователем» — строки для плашки под шапкой. */
export const freshDataTexts = {
  /** «Данные обновлены · Лена Усова, пакет #128 · 3 правки, 1 из них в брендах, которые вы правите» */
  text: (author: string, changeSetId: number, changes: number, mine: number) => {
    const head = `Данные обновлены · ${author}, пакет #${changeSetId}`
    if (changes <= 0) return head
    const tail = mine > 0
      ? `${countOf(changes, forms.change)}, ${mine} из них в брендах, которые вы правите`
      : countOf(changes, forms.change)
    return `${head} · ${tail}`
  },
  view: 'Посмотреть',
  refresh: 'Обновить',
  dismiss: 'Скрыть плашку',
  /** «Посмотреть» открывает пакет в новой вкладке (§11.7). */
  href: (changeSetId: number) => `/history/${changeSetId}`,
}

/** Названия каналов для подписи правки: берём из bootstrap, но нужен и запас. */
export const channelFallbackNames: Record<ChannelCode, string> = {
  retail: 'Розница',
  b2b: 'Опт',
}

// ──────────────────── §8.1 «С вашего прошлого визита» ───────────────────────

/** Названия через запятую плюс «…», когда список длиннее трёх (§8.1). */
export function namesPreview(names: string[], limit = 3): string {
  const head = names.slice(0, limit).join(', ')
  return names.length > limit ? `${head} …` : head
}

export const digestTexts = {
  /** §12.2: «С вашего прошлого визита — 2 октября, 11:40» */
  title: 'С вашего прошлого визита',
  titleWith: (when: string) => `С вашего прошлого визита — ${when}`,
  noMark: 'Метки прошлого визита ещё нет — показываем изменения за последние 7 дней',

  openTable: 'Открыть таблицу →',
  openTableHint: 'Метка «прошлый визит» сдвинется только по этой кнопке',

  loadingLabel: 'Собираем сводку',
  errorTitle: 'Не удалось собрать сводку',
  retry: 'Повторить',

  stats: {
    changes: (n: number) => plural(n, forms.change),
    packs: (n: number) => plural(n, forms.changeSet),
    authors: (n: number) => plural(n, ['автор', 'автора', 'авторов']),
    newBrands: (n: number) => `${plural(n, ['новый', 'новых', 'новых'])} ${plural(n, forms.brand)}`,
    noUrl: 'без URL',
    noUrlTitle: (channel: string) => `Бренды канала ${channel} без ссылки на страницу`,
    noUrlWas: (n: number) => `было ${n}`,
  },

  byType: 'По типам',
  authors: 'Авторы',
  worth: 'Что стоит посмотреть',
  show: 'показать →',

  packs: 'Пакеты',
  packsHint: 'как релиз-ноты',
  expand: 'раскрыть',
  collapse: 'свернуть',
  expandLabel: (id: number) => `Раскрыть пакет #${id}`,
  collapseLabel: (id: number) => `Свернуть пакет #${id}`,
  limited: (n: number) => `Подробности раскрываются у первых ${n} ${plural(n, forms.changeSet)} — остальное в журнале`,
  toHistory: 'Открыть журнал',

  emptyTitle: 'С прошлого визита ничего не менялось',
  emptyText: (when: string) =>
    `С ${when} никто не публиковал правок. Системный пакет импорта и откаты в сводку не попадают.`,

  excluded: 'Системный пакет импорта и откаты в сводку не попадают',

  insight: {
    tierUp: (n: number, names: string) =>
      `${countOf(n, forms.brand)} ${plural(n, ['поднялся', 'поднялись', 'поднялись'])} до A+ — ${names}`,
    hidden: (n: number, names: string) =>
      `${countOf(n, forms.brand)} ${plural(n, ['скрыт', 'скрыты', 'скрыты'])} с сайта — ${names}`,
    topWithoutUrl: (n: number) => `${countOf(n, forms.brand)} A+/A всё ещё без URL`,
    comments: (n: number, names: string) =>
      `${countOf(n, forms.brand)} с новыми комментариями — ${names}`,
    newBrands: (n: number, names: string) =>
      `${countOf(n, forms.brand)} ${plural(n, ['добавлен', 'добавлены', 'добавлены'])} — ${names}`,
  },
} as const
